import { useEffect, useReducer, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, UploadCloud, Play, Rocket, Download, Link as LinkIcon, Plus, X, ImagePlus, RefreshCw } from 'lucide-react'
import ConfirmDialog from '../components/ui/ConfirmDialog.jsx'
import ShareLinkModal from '../components/editor/ShareLinkModal.jsx'
import { Share2 } from 'lucide-react'
import AppShell from '../components/AppShell.jsx'
import Button from '../components/ui/Button.jsx'
import Input, { Field } from '../components/ui/Input.jsx'
import Skeleton from '../components/ui/Skeleton.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { useAutosave } from '../hooks/useAutosave.js'
import StepPanel from '../components/editor/StepPanel.jsx'
import PageManager from '../components/editor/PageManager.jsx'
import SampleBookButton from '../components/editor/SampleBookButton.jsx'
import LinkEditorModal from '../components/editor/LinkEditorModal.jsx'
import PublishedScreen from '../components/editor/PublishedScreen.jsx'
import { getProject, getSignedUrls, publishProject, saveProjectManifest, deleteProject } from '../lib/api.js'
import { uploadAllAssets, buildPersistManifest, hydrateStateFromManifest, collectDraftKeys } from '../lib/manifestPersist.js'

const FlipIntake = () => window.FlipIntake
const FlipBookCtor = () => window.FlipBook

const BG_MODES = [
  { value: 'dark', label: 'Dark studio' },
  { value: 'black', label: 'Pure black' },
  { value: 'light', label: 'Light' },
  { value: 'tinted', label: 'Tinted (from accent)' }
]
const FLIP_STYLES = [
  { value: 'soft', label: 'Soft (default)' },
  { value: 'crisp', label: 'Crisp flick' },
  { value: 'paper', label: 'Paper rustle' },
  { value: 'riffle', label: 'Riffle' },
  { value: 'thick', label: 'Thick page' },
  { value: 'none', label: 'None (silent)' }
]

export default function ProjectEditor() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const stateRef = useRef(null)
  if (!stateRef.current) stateRef.current = FlipIntake().createState()
  const [, bump] = useReducer((x) => x + 1, 0)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [processing, setProcessing] = useState(null) // {p, text}
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState(null)
  const [sampleLoading, setSampleLoading] = useState(false)
  const [step, setStep] = useState(1)
  const [linkEditorOpen, setLinkEditorOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishedUrl, setPublishedUrl] = useState(null)
  const [publishing, setPublishing] = useState(false)
  const [downloadingZip, setDownloadingZip] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fbHostRef = useRef(null)
  const fbInstanceRef = useRef(null)
  const dropRef = useRef(null)
  const fileInputRef = useRef(null)
  const appendInputRef = useRef(null)
  const replaceInputRef = useRef(null)
  const [pendingReplaceFiles, setPendingReplaceFiles] = useState(null)

  const saveDraft = async () => {
    const s = stateRef.current
    await saveProjectManifest(projectId, buildPersistManifest(FlipIntake(), s), { title: s.title })
  }
  const { status: saveStatus, markDirty } = useAutosave(saveDraft)

  // ── Load existing project (draft or published) ────────────────
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const project = await getProject(projectId)
        if (cancelled) return
        const manifest = project.manifest || {}
        const s = stateRef.current
        if (Array.isArray(manifest.pages) && manifest.pages.length) {
          const keys = collectDraftKeys(manifest)
          const urls = keys.length ? await getSignedUrls(projectId, keys) : {}
          if (cancelled) return
          hydrateStateFromManifest(s, manifest, urls)
          setStep(4) // fully loaded — all steps "done", nothing locked
          renderPreview()
        } else {
          s.title = project.title || ''
        }
        setLoading(false)
      } catch (err) {
        // Never leave the user staring at a skeleton forever — surface the
        // real error with a way back, instead of an infinite loading state.
        if (!cancelled) { setLoadError(err.message || 'Could not load this project.'); setLoading(false) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  // The preview host only mounts once `loading` is false; the render call
  // inside load() runs while it's still null, so (re)render here once it exists.
  useEffect(() => {
    if (!loading && stateRef.current.pages.length) renderPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  // Stop preview music / free the flip engine when leaving the editor.
  useEffect(() => () => { fbInstanceRef.current?.destroy() }, [])

  function renderPreview() {
    const host = fbHostRef.current
    if (!host) return
    if (fbInstanceRef.current) { fbInstanceRef.current.destroy(); fbInstanceRef.current = null }
    host.innerHTML = ''
    const mount = document.createElement('div')
    mount.style.cssText = 'position:absolute;inset:0'
    host.appendChild(mount)
    fbInstanceRef.current = new (FlipBookCtor())(mount, FlipIntake().buildManifest(stateRef.current, false), {})
  }

  async function startUpload() {
    setUploading(true)
    setUploadProgress(0)
    setUploadError(null)
    try {
      await uploadAllAssets(stateRef.current, projectId, setUploadProgress)
      markDirty()
    } catch (err) {
      // A transient toast alone isn't enough here — cloud storage not being
      // configured yet is a real, recoverable state the reseller needs a
      // clear way back from, not just a message that vanishes in 3 seconds.
      setUploadError(err.message)
      toast('Some assets failed to upload — see the panel below', 'error')
    } finally {
      setUploading(false)
    }
  }

  async function handleFiles(fileList) {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setProcessing({ p: 0, text: 'Reading document…' })
    try {
      await FlipIntake().ingestFiles(stateRef.current, files, { onProgress: (p, text) => setProcessing({ p, text }) })
      setProcessing(null)
      setStep(2)
      bump()
      renderPreview()
      startUpload()
    } catch (err) {
      setProcessing(null)
      toast(err.message, 'error')
    }
  }

  async function handleAppendImages(fileList) {
    const files = Array.from(fileList || []).filter((f) => /^image\//.test(f.type))
    if (!files.length) return
    setProcessing({ p: 0, text: 'Adding images…' })
    try {
      await FlipIntake().appendImages(stateRef.current, files, { onProgress: (p, text) => setProcessing({ p, text }) })
      setProcessing(null)
      bump()
      renderPreview()
      markDirty()
      startUpload()
      toast(`Added ${files.length} page${files.length > 1 ? 's' : ''}`, 'success')
    } catch (err) {
      setProcessing(null)
      toast(err.message, 'error')
    }
  }

  async function handleReplaceSource(fileList) {
    // handleFiles() fully replaces state.pages — confirmed destructive via
    // ConfirmDialog before we ever get here (see the "Replace source" button).
    await handleFiles(fileList)
  }

  async function handleDeleteProject() {
    setDeleting(true)
    try {
      await deleteProject(projectId)
      toast('Project deleted', 'success')
      navigate(-1)
    } catch (err) {
      toast(err.message, 'error')
      setDeleting(false)
    }
  }

  async function handleLoadSample(url) {
    setSampleLoading(true)
    try {
      const file = await FlipIntake().fetchAsFile(url)
      await handleFiles([file])
    } catch (err) {
      toast('Could not load the sample book: ' + err.message, 'error')
    } finally {
      setSampleLoading(false)
    }
  }

  function mutate(fn) {
    fn(stateRef.current)
    bump()
    renderPreview()
    markDirty()
  }

  async function handleAssetFile(kind, file) {
    const reader = new FileReader()
    reader.onload = async () => {
      const s = stateRef.current
      if (kind === 'logo') s.logo = reader.result
      if (kind === 'bg') s.bgImage = reader.result
      if (kind === 'audio') s.audio = { name: file.name, dataUrl: reader.result, ext: (file.name.match(/\.[a-z0-9]+$/i) || ['.mp3'])[0] }
      bump(); renderPreview()
      await startUpload()
    }
    reader.readAsDataURL(file)
  }

  function handlePreviewSound(style) {
    if (style !== 'none') FlipBookCtor().previewSound?.(style)
  }

  async function handlePublish() {
    setPublishing(true)
    try {
      await saveDraft()
      const { url } = await publishProject(projectId)
      setPublishedUrl(url)
      setPublishOpen(true)
    } catch (err) {
      toast('Publish failed: ' + err.message, 'error')
    } finally {
      setPublishing(false)
    }
  }

  async function handleDownloadZip() {
    setDownloadingZip(true)
    try {
      const s = stateRef.current
      // Re-hydrated drafts hold signed URLs, not live blobs — fetch bytes
      // back before zipping if needed.
      for (const p of s.pages) {
        if (!p.blob) { p.blob = await fetch(p.url).then((r) => r.blob()) }
        if (!p.thumbBlob) { p.thumbBlob = await fetch(p.thumbUrl).then((r) => r.blob()) }
      }
      const result = await FlipIntake().buildExportZip(s, { assetBase: '/' })
      FlipIntake().downloadBlob(result.blob, result.filename)
    } catch (err) {
      toast('Export failed: ' + err.message, 'error')
    } finally {
      setDownloadingZip(false)
    }
  }

  const s = stateRef.current
  const hasPages = s.pages.length > 0
  // A single rule for all three steps: the currently-open one is 'active',
  // anything before it is 'done' (collapsed, reopenable), anything after is
  // 'locked'. step=4 is the "everything configured" sentinel, so with no
  // pages yet only step 1 can ever be active.
  const stepState = (n) => {
    if (!hasPages) return n === 1 ? 'active' : 'locked'
    if (step === n) return 'active'
    return n < step ? 'done' : 'locked'
  }
  const step1State = stepState(1)
  const step2State = stepState(2)
  const step3State = stepState(3)

  if (loading) {
    return (
      <AppShell>
        <Skeleton className="h-8 w-64 mb-6" />
        <Skeleton className="h-96 w-full" />
      </AppShell>
    )
  }

  if (loadError) {
    return (
      <AppShell>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-muted hover:text-ink text-[13px] mb-4 transition-colors">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-lg border border-danger/30 bg-danger/5 p-6 max-w-lg">
          <h2 className="font-display font-bold text-[16px] mb-1.5">Couldn't load this project</h2>
          <p className="text-muted text-[13.5px] mb-4">{loadError}</p>
          <Button variant="ghost" onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-muted hover:text-ink text-[13px] mb-4 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid gap-8" style={{ gridTemplateColumns: '380px 1fr' }}>
        {/* ── Sidebar wizard ──────────────────────────────────── */}
        <div className="rounded-lg border border-line bg-panel shadow-elev1 flex flex-col">
          <div className="px-5 py-4 border-b border-line flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue" />
            <span className="text-[12px] font-medium text-muted">
              {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved ✓' : saveStatus === 'error' ? 'Save failed' : hasPages ? 'Draft' : ''}
            </span>
          </div>

          <div className="px-5 flex-1 overflow-y-auto">
            <StepPanel n={1} title="Source" state={step1State} summary={`${s.pages.length} pages · ${s.sourceKind === 'pdf' ? 'PDF' : 'Images'}`} onReopen={() => setStep(1)}>
              {!hasPages ? (
                <>
                  <div
                    ref={dropRef}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add('!border-blue') }}
                    onDragLeave={() => dropRef.current?.classList.remove('!border-blue')}
                    onDrop={(e) => { e.preventDefault(); dropRef.current?.classList.remove('!border-blue'); handleFiles(e.dataTransfer.files) }}
                    className="border-[1.5px] border-dashed border-line rounded-lg p-6 text-center cursor-pointer transition-colors duration-150 bg-panel2 hover:border-blue/60"
                  >
                    <UploadCloud size={24} className="mx-auto mb-2 text-blue" />
                    <div className="font-semibold text-[13px] mb-0.5">Drop a PDF or images</div>
                    <div className="text-[11.5px] text-muted-2">or click to browse — pages become a flipbook</div>
                  </div>
                  <input ref={fileInputRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} />
                  <div className="flex justify-center mt-3">
                    <SampleBookButton onLoad={handleLoadSample} loading={sampleLoading} />
                  </div>

                  {processing && (
                    <div className="mt-4">
                      <div className="h-1.5 rounded-full bg-panel2 overflow-hidden mb-2">
                        <motion.div className="h-full bg-gradient-to-r from-blue to-sky" animate={{ width: `${processing.p * 100}%` }} transition={{ duration: 0.2 }} />
                      </div>
                      <div className="text-[12px] text-muted text-center">{processing.text}</div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <PageManager
                    pages={s.pages}
                    busy={uploading}
                    onReorder={(from, to) => mutate((st) => FlipIntake().reorderPages(st, from, to))}
                    onDelete={(idx) => mutate((st) => FlipIntake().deletePage(st, idx))}
                    onRotate={async (idx) => {
                      await FlipIntake().rotatePage(s.pages[idx], 90)
                      bump(); renderPreview(); markDirty(); startUpload()
                    }}
                  />

                  {processing && (
                    <div className="mt-3">
                      <div className="h-1.5 rounded-full bg-panel2 overflow-hidden mb-2">
                        <motion.div className="h-full bg-gradient-to-r from-blue to-sky" animate={{ width: `${processing.p * 100}%` }} transition={{ duration: 0.2 }} />
                      </div>
                      <div className="text-[12px] text-muted text-center">{processing.text}</div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-3">
                    <input ref={appendInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { handleAppendImages(e.target.files); e.target.value = '' }} />
                    <Button variant="ghost" size="sm" icon={ImagePlus} className="flex-1" onClick={() => appendInputRef.current?.click()}>
                      Add images
                    </Button>
                    <input ref={replaceInputRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={(e) => { setPendingReplaceFiles(e.target.files); e.target.value = '' }} />
                    <Button variant="ghost" size="sm" icon={RefreshCw} className="flex-1" onClick={() => replaceInputRef.current?.click()}>
                      Replace source
                    </Button>
                  </div>
                </>
              )}
            </StepPanel>

            <StepPanel n={2} title="Details" state={step2State} summary={s.title || 'Untitled'} onReopen={() => setStep(2)}>
              <Field label="Title">
                <Input value={s.title} onChange={(e) => mutate((st) => { st.title = e.target.value })} placeholder="My Flipbook" />
              </Field>
              <Field label="Accent colour">
                <div className="flex gap-2 flex-wrap">
                  {s.palette.map((c) => (
                    <button key={c} onClick={() => mutate((st) => { st.accent = c })}
                      className="w-7 h-7 rounded-md border-2 transition-transform hover:scale-110"
                      style={{ background: c, borderColor: s.accent === c ? '#fff' : 'transparent' }} />
                  ))}
                </div>
              </Field>
              <Field label="Background">
                <select value={s.bgmode} onChange={(e) => mutate((st) => { st.bgmode = e.target.value })}
                  className="w-full h-11 px-3 bg-panel2 border border-line rounded-md text-[13.5px]">
                  {BG_MODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <label className="flex items-center gap-2.5 text-[13px] cursor-pointer py-1">
                <input type="checkbox" checked={s.showCover} onChange={(e) => mutate((st) => { st.showCover = e.target.checked })} />
                First page is a solo cover
              </label>
              <Field label="Page-turn sound">
                <div className="flex gap-2">
                  <select value={s.flipSoundStyle} onChange={(e) => mutate((st) => { st.flipSoundStyle = e.target.value })}
                    className="flex-1 h-11 px-3 bg-panel2 border border-line rounded-md text-[13.5px]">
                    {FLIP_STYLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <Button variant="ghost" size="sm" onClick={() => handlePreviewSound(s.flipSoundStyle)}>▶</Button>
                </div>
              </Field>
              <Button size="sm" className="w-full" onClick={() => setStep(3)}>Continue</Button>
            </StepPanel>

            <StepPanel n={3} title="Branding &amp; extras" state={step3State} summary={`${s.pages.reduce((n, p) => n + (p.links?.length || 0), 0)} links`} onReopen={() => setStep(3)}>
              <Field label="Logo (optional)">
                <AssetPicker current={s.logo} onPick={(f) => handleAssetFile('logo', f)} onClear={() => mutate((st) => { st.logo = null })} accept="image/*" />
              </Field>
              <Field label="Background image (optional)">
                <AssetPicker current={s.bgImage} onPick={(f) => handleAssetFile('bg', f)} onClear={() => mutate((st) => { st.bgImage = null })} accept="image/*" />
              </Field>
              <Field label="Background music (optional)">
                <AssetPicker current={s.audio?.name} onPick={(f) => handleAssetFile('audio', f)} onClear={() => mutate((st) => { st.audio = null })} accept="audio/*" isFile />
              </Field>
              <Field label="Interactive links">
                <Button variant="ghost" size="sm" icon={LinkIcon} className="w-full" disabled={!hasPages} onClick={() => setLinkEditorOpen(true)}>
                  Edit links &amp; hotspots
                </Button>
              </Field>
              <Field label="Analytics endpoint (optional)" hint="Views & interactions POST here (opt-in).">
                <Input type="url" value={s.analytics} onChange={(e) => mutate((st) => { st.analytics = e.target.value.trim() })} placeholder="https://…/collect" />
              </Field>
              <Button size="sm" className="w-full" onClick={() => setStep(4)}>Done</Button>
            </StepPanel>
          </div>

          <div className="p-5 border-t border-line space-y-2.5">
            {uploading && (
              <div className="text-[11.5px] text-muted-2 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-panel2 overflow-hidden">
                  <div className="h-full bg-blue transition-all duration-200" style={{ width: `${uploadProgress * 100}%` }} />
                </div>
                Uploading…
              </div>
            )}
            {uploadError && !uploading && (
              <div className="rounded-md border border-warn/30 bg-warn/10 p-3">
                <p className="text-[12.5px] text-ink font-medium mb-0.5">Cloud storage isn't ready yet</p>
                <p className="text-[11.5px] text-muted leading-relaxed mb-2">
                  Your pages are safe in this browser — nothing is lost. Once storage is connected, click retry to finish saving to the cloud.
                </p>
                <Button variant="ghost" size="sm" className="w-full" onClick={startUpload}>
                  Retry upload
                </Button>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" icon={Share2} onClick={() => setShareOpen(true)} disabled={!hasPages}>
                Share
              </Button>
              <Button variant="ghost" className="flex-1" icon={Download} onClick={handleDownloadZip} loading={downloadingZip} disabled={!hasPages}>
                .zip
              </Button>
            </div>
            <Button className="w-full" icon={Rocket} onClick={handlePublish} loading={publishing} disabled={!hasPages || uploading}>
              Publish
            </Button>
            <p className="text-[11px] text-muted-2 leading-relaxed">
              Publishing hosts this book at a permanent URL. Share creates a private, optionally-expiring link instead.
            </p>
            <button
              onClick={() => setConfirmDeleteOpen(true)}
              className="text-[12px] text-muted-2 hover:text-danger transition-colors w-full text-center pt-1"
            >
              Delete this project
            </button>
          </div>
        </div>

        {/* ── Live preview ────────────────────────────────────── */}
        <div className="rounded-lg border border-line bg-panel overflow-hidden relative" style={{ minHeight: 560 }}>
          {!hasPages ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-10 text-muted">
              <UploadCloud size={56} className="text-line" />
              <h2 className="font-display font-bold text-[19px] text-ink">Your flipbook preview appears here</h2>
              <p className="text-[13.5px] max-w-[380px] leading-relaxed">Drop a PDF or try the sample book to see FlipStudio render pages, extract text for search, and pick a colour theme — automatically.</p>
            </div>
          ) : (
            <div ref={fbHostRef} className="absolute inset-0" />
          )}
        </div>
      </div>

      {hasPages && (
        <LinkEditorModal
          open={linkEditorOpen}
          onClose={() => { setLinkEditorOpen(false); bump(); renderPreview(); markDirty(); startUpload() }}
          pages={s.pages}
          FlipIntake={FlipIntake()}
          onChange={() => bump()}
        />
      )}

      <PublishedScreen
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        url={publishedUrl}
        title={s.title}
        onDownloadZip={handleDownloadZip}
        downloadingZip={downloadingZip}
      />

      {hasPages && <ShareLinkModal open={shareOpen} onClose={() => setShareOpen(false)} projectId={projectId} />}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteProject}
        title="Delete this project?"
        description={`"${s.title || 'Untitled'}" and all its pages will be permanently deleted. This can't be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
      />

      <ConfirmDialog
        open={pendingReplaceFiles !== null}
        onClose={() => setPendingReplaceFiles(null)}
        onConfirm={() => { const files = pendingReplaceFiles; setPendingReplaceFiles(null); handleReplaceSource(files) }}
        title="Replace the source file?"
        description="This removes every current page and rebuilds the book from the new file. Branding, links and settings you've already added will be lost. This can't be undone."
        confirmLabel="Replace"
      />
    </AppShell>
  )
}

function AssetPicker({ current, onPick, onClear, accept, isFile }) {
  const ref = useRef(null)
  return (
    <div>
      <input ref={ref} type="file" accept={accept} hidden onChange={(e) => { const f = e.target.files[0]; if (f) onPick(f) }} />
      {!current ? (
        <Button variant="ghost" size="sm" icon={Plus} className="w-full" onClick={() => ref.current?.click()}>Add</Button>
      ) : (
        <div className="flex items-center gap-2">
          {!isFile && <img src={current} className="h-8 rounded-sm border border-line" />}
          {isFile && <span className="text-[12.5px] text-muted flex-1 truncate">♪ {current}</span>}
          <button onClick={onClear} className="text-muted-2 hover:text-danger transition-colors"><X size={15} /></button>
        </div>
      )}
    </div>
  )
}
